import { Radar } from "lucide-react";
import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DropOffSearch } from "./KindPicker";
import { OzonModal } from "./OzonModal";
import {
  SEARCH_OPTION,
  dropOffBadge,
  dropOffOptions,
  shownDropOff,
} from "./dropOff";
import {
  DAYS_DEFAULT,
  DAYS_MAX,
  DAYS_MIN,
  HOURS_DEFAULT,
  HOURS_MAX,
  notBeforeAt,
  parseWatchForm,
  type WatchDropOff,
} from "./slotWatch";

/** Пункт по умолчанию: выбранный у кросс-дока направления, иначе привычный компании — он первый
 *  в списке `/drop-offs` (то же правило, что у `KindPicker`). */
function defaultDropOff(
  d: ozon.OzonDirection,
  dropOffs: ozon.OzonDropOff[],
): WatchDropOff | null {
  if (d.kind === "crossdock") {
    const s = shownDropOff(d, dropOffs);
    if (s) return s;
  }
  const first = dropOffs[0];
  return first ? { id: first.id, name: first.name, type: first.type } : null;
}

/** Постановка поставки на мониторинг мест: пункт сдачи кросс-дока, «не раньше чем через N ч», срок.
 *
 *  ⚠️ Ошибка постановки (409/422 с причиной по-русски) — ВНУТРИ окна, и окно остаётся открытым:
 *  причина обычно про ввод («пункт не кросс-док», «уже на мониторинге»), и закрыть окно значило бы
 *  заставить набирать всё заново.
 */
export function SlotWatchDialog({
  d,
  dropOffs,
  dropOffsError,
  pending,
  error,
  onSubmit,
  onClose,
  now = Date.now(),
}: {
  d: ozon.OzonDirection;
  dropOffs: ozon.OzonDropOff[];
  /** список пунктов не загрузился — пункт можно найти поиском */
  dropOffsError: string | null;
  pending: boolean;
  /** отказ бэкенда — текстом */
  error: string | null;
  onSubmit: (body: ozon.SlotWatchStart) => void;
  onClose: () => void;
  now?: number;
}): React.ReactElement {
  const [chosen, setChosen] = useState<WatchDropOff | null>(null);
  // найденная поиском точка встаёт в список отдельной опцией — иначе `<select>` показал бы другую
  const [found, setFound] = useState<WatchDropOff | null>(null);
  const [searching, setSearching] = useState(false);
  const [hours, setHours] = useState(String(HOURS_DEFAULT));
  const [days, setDays] = useState(String(DAYS_DEFAULT));
  const [tried, setTried] = useState(false);

  const pick = chosen ?? defaultDropOff(d, dropOffs);
  const options = dropOffOptions(d, dropOffs);
  if (found && !options.some((o) => o.id === found.id))
    options.unshift({ id: found.id, label: found.name ?? `пункт #${found.id}`, name: found.name });
  if (pick && !options.some((o) => o.id === pick.id))
    options.unshift({ id: pick.id, label: pick.name ?? `пункт #${pick.id}`, name: pick.name });

  const byId = (id: number): WatchDropOff | null => {
    if (found?.id === id) return found;
    if (pick?.id === id) return pick;
    const p = dropOffs.find((x) => x.id === id);
    if (p) return { id: p.id, name: p.name, type: p.type };
    const s = shownDropOff(d, dropOffs);
    return s && s.id === id ? s : null;
  };

  const parsed = parseWatchForm({ dropOff: pick, hours, days });
  const errs = tried ? (parsed.errors ?? {}) : {};
  const hoursN = /^\d+$/.test(hours.trim()) ? Number(hours.trim()) : null;
  const badge = dropOffBadge(pick?.type ?? null);

  const submit = (): void => {
    setTried(true);
    if (parsed.body) onSubmit(parsed.body);
  };

  return (
    // ⚠️ пока запрос постановки в пути, закрыть окно нельзя (Esc, подложка, крестик): ответ пришёл
    // бы в закрытый диалог, а ошибку никто бы не увидел
    <OzonModal title={`Мониторинг мест — ${d.region}`} onClose={pending ? noop : onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          {d.legal_name}. Раз в минуту наблюдатель смотрит окна приёмки у пункта сдачи и, как только
          появится подходящее, сам оформит заявку.
        </p>

        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Пункт сдачи (кросс-док)</span>
          <span className="flex flex-wrap items-center gap-1">
            <select
              aria-label="Пункт сдачи"
              className="h-8 min-w-0 max-w-full flex-1 rounded border border-input bg-background px-1 text-sm"
              value={searching ? SEARCH_OPTION : (pick?.id ?? "")}
              disabled={pending}
              onChange={(e) => {
                if (e.target.value === SEARCH_OPTION) {
                  setSearching(true);
                  return;
                }
                setSearching(false);
                setChosen(byId(Number(e.target.value)));
              }}
            >
              {pick == null && <option value="">— пункт сдачи —</option>}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              <option value={SEARCH_OPTION}>Другой пункт / ПВЗ…</option>
            </select>
            {badge && (
              <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                {badge}
              </span>
            )}
          </span>
        </label>
        {searching && (
          <div className="rounded border p-2 text-xs">
            <DropOffSearch
              onPick={(p) => {
                const w = { id: p.id, name: p.name, type: p.type };
                setFound(w);
                setChosen(w);
                setSearching(false);
              }}
            />
          </div>
        )}
        {dropOffsError && (
          <p className="text-xs text-destructive">
            Список пунктов сдачи не загрузился: {dropOffsError}. Пункт можно найти поиском.
          </p>
        )}
        {errs.dropOff && <FieldError>{errs.dropOff}</FieldError>}

        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="watch-hours">Искать окна не раньше чем через</label>
            <input
              id="watch-hours"
              type="number"
              inputMode="numeric"
              min={0}
              max={HOURS_MAX}
              value={hours}
              disabled={pending}
              onChange={(e) => setHours(e.target.value)}
              className={cn(
                "h-8 w-20 rounded border bg-background px-2 tabular-nums",
                errs.hours ? "border-destructive" : "border-input",
              )}
            />
            <span>ч</span>
            {hoursN !== null && hoursN <= HOURS_MAX && (
              <span className="text-xs text-muted-foreground" title="Если окно найдётся прямо сейчас">
                {notBeforeAt(hoursN, now)} по вашему времени
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Считается от момента каждой проверки: окно раньше этого наблюдатель не возьмёт.
          </p>
          {errs.hours && <FieldError>{errs.hours}</FieldError>}
        </div>

        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="watch-days">Следить</label>
            <input
              id="watch-days"
              type="number"
              inputMode="numeric"
              min={DAYS_MIN}
              max={DAYS_MAX}
              value={days}
              disabled={pending}
              onChange={(e) => setDays(e.target.value)}
              className={cn(
                "h-8 w-16 rounded border bg-background px-2 tabular-nums",
                errs.days ? "border-destructive" : "border-input",
              )}
            />
            <span>дней</span>
          </div>
          {errs.days && <FieldError>{errs.days}</FieldError>}
        </div>

        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          Перенос окон не делается. Найдётся окно — портал сам создаст черновик и заявку на самое
          раннее подходящее окно; если Ozon возьмёт меньше, заявка всё равно создаётся.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            ⚠ {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
            Отмена
          </Button>
          <Button size="sm" onClick={submit} disabled={pending || searching}>
            <Radar className={cn("size-4", pending && "animate-pulse")} />
            Поставить на мониторинг
          </Button>
        </div>
      </div>
    </OzonModal>
  );
}

function noop(): void {}

function FieldError({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-xs text-destructive">⚠ {children}</p>;
}
